// emailContacts.js
// Direct company-scoped Firestore management of the email contact book. Contacts
// use the same direct authenticated model as Function and Trigger editing — there
// is no HTTP endpoint. Firestore security rules enforce tenant isolation and the
// restore lock; `email_operations` remains the authoritative pre-send validator,
// which independently repeats address/header/status validation before any
// provider submission. The client-side validation here is only for immediate
// feedback.
//
// Contacts live at `users/{companyId}/contacts/{contactId}` where `contactId` is
// a UUID version 4 minted at creation (spec section 2.1). Identity is therefore
// stable: the address is an ordinary mutable field, so correcting it updates the
// SAME document and touches nothing else — no replacement contact, no Email
// Output rewrite, no Function invalidation, no Trigger reset. Address creation
// and changes atomically maintain contact_addresses ownership records; lifecycle
// writes keep that ownership.
//
// The service takes `companyId` and the actor's Firebase uid as arguments,
// matching the house Firestore-service pattern (see services/cleaningGestures.js); the
// caller supplies them from the authenticated React context. It never accepts an
// arbitrary company override and never sets `allow_email = true` anywhere.

import {
  collection,
  doc,
  getDocFromServer,
  getDocsFromServer,
  onSnapshot,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';

const MAX_DISPLAY_NAME_LENGTH = 80;
const CONTACT_CHANGED_MESSAGE = 'This contact changed in another session. Close this dialog and try again.';

// An ASCII-only subset of the backend's `canonicalize_mailbox` contract. The
// browser additionally limits local parts to 64 characters and excludes encoded
// international domains, so it never needs to duplicate the backend's Unicode
// domain validation. Neither stored form converts the address to another one.
//
// dot-atom local part: atoms of RFC 5322 atext, separated by single dots, with
// no leading, trailing, or consecutive dot. Quoted local parts are not accepted
// (the backend validates with quoting disabled).
const LOCAL_ATOM = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+$/;
// Hostname label: ASCII letters/digits/hyphen, never starting or ending with a
// hyphen. Encoded international labels (`xn--…`) are rejected separately.
const DOMAIN_LABEL = /^[A-Za-z0-9]([A-Za-z0-9-]*[A-Za-z0-9])?$/;
// `test` is deliberately absent: canonicalize_mailbox uses test_environment=True.
const SPECIAL_USE_DOMAINS = new Set(['arpa', 'invalid', 'local', 'localhost', 'onion']);

const MAX_EMAIL_LENGTH = 254;
const MAX_LOCAL_PART_LENGTH = 64;
const MAX_DOMAIN_LENGTH = 255;
const MAX_DOMAIN_LABEL_LENGTH = 63;

// Bounded, user-readable rejections — one per class the contract distinguishes.
const ERRORS = {
  empty: 'Please enter an email address.',
  nonAscii: 'Email addresses must use ASCII characters only — Unicode addresses and domains are not supported.',
  control: 'Email addresses must not contain line breaks or control characters.',
  list: 'Please enter exactly one email address.',
  comment: 'Email addresses must not contain comments in parentheses.',
  displayName: 'Enter the address only, without a display name.',
  quoted: 'Quoted email addresses are not supported.',
  internationalDomain: 'International domain names are not supported in email addresses.',
  invalid: 'Please enter a valid email address.',
};

// --- the shared canonicalizer ----------------------------------------------

// Validate exactly one ASCII addr-spec and return both stored forms:
// `email` (the trimmed address as typed) and `email_normalized` (its ASCII
// lowercase form). Throws a bounded Error for every rejected class. This is the
// single validation entry point: create, address edit, the duplicate lookup, and
// the Settings forms all call it.
export function canonicalizeEmail(rawEmail) {
  if (typeof rawEmail !== 'string') {
    throw new Error(ERRORS.empty);
  }
  // Both scans run over the RAW value, in the backend's order, so the browser
  // and the backend reject the same inputs for the same reason.
  const codePoints = Array.from(rawEmail, (character) => character.codePointAt(0));
  // Every non-ASCII code point: a Unicode local part, a Unicode domain, or
  // non-ASCII whitespace (no-break space, em space) is rejected outright rather
  // than converted or silently stripped.
  if (codePoints.some((code) => code > 127)) {
    throw new Error(ERRORS.nonAscii);
  }
  // Control characters anywhere, which covers CR/LF header injection.
  if (codePoints.some((code) => code < 32 || code === 127)) {
    throw new Error(ERRORS.control);
  }

  // Every surviving character is printable ASCII, so trim() removes exactly the
  // leading/trailing ASCII spaces the contract allows.
  const email = rawEmail.trim();
  if (!email) {
    throw new Error(ERRORS.empty);
  }
  if (email.length > MAX_EMAIL_LENGTH) {
    throw new Error(ERRORS.invalid);
  }
  if (/[,;]/.test(email)) {
    throw new Error(ERRORS.list);
  }
  if (/[()]/.test(email)) {
    throw new Error(ERRORS.comment);
  }
  if (email.includes('"')) {
    throw new Error(ERRORS.quoted);
  }
  if (/[<> ]/.test(email)) {
    throw new Error(ERRORS.displayName);
  }

  const atoms = email.split('@');
  if (atoms.length !== 2) {
    throw new Error(ERRORS.invalid);
  }
  const [localPart, domain] = atoms;

  if (
    !localPart
    || localPart.length > MAX_LOCAL_PART_LENGTH
    || !localPart.split('.').every((atom) => LOCAL_ATOM.test(atom))
  ) {
    throw new Error(ERRORS.invalid);
  }

  const labels = domain.toLowerCase().split('.');
  if (labels.some((label) => label.startsWith('xn--'))) {
    throw new Error(ERRORS.internationalDomain);
  }
  if (
    !domain
    || domain.length > MAX_DOMAIN_LENGTH
    || labels.length < 2
    || !labels.every((label) => label.length <= MAX_DOMAIN_LABEL_LENGTH && DOMAIN_LABEL.test(label))
    || labels.some((label) => label.slice(2, 4) === '--')
    || !/[A-Za-z]$/.test(domain)
    || SPECIAL_USE_DOMAINS.has(labels[labels.length - 1])
  ) {
    throw new Error(ERRORS.invalid);
  }

  // The value is ASCII by construction, so toLowerCase() is ASCII lowercase —
  // identical to the backend's `email.lower()` over the accepted character set.
  return { email, email_normalized: email.toLowerCase() };
}

// Trim + validate a display name used in the "Display Name <address>" header.
function cleanDisplayName(rawName) {
  const value = String(rawName ?? '');
  if (/[\r\n]/.test(value)) {
    throw new Error('Display name must not contain line breaks.');
  }
  const trimmed = value.trim();
  if (!trimmed) {
    throw new Error('Please enter a display name.');
  }
  if (trimmed.length > MAX_DISPLAY_NAME_LENGTH) {
    throw new Error(`Display name must be ${MAX_DISPLAY_NAME_LENGTH} characters or fewer.`);
  }
  return trimmed;
}

// --- reads ------------------------------------------------------------------

const toContact = (contactDoc) => ({ id: contactDoc.id, ...contactDoc.data() });

// Contact lists feed recipient review and permission dialogs. Require the
// server so an unavailable read cannot approve stale cached recipients.
export async function list(companyId) {
  const snapshot = await getDocsFromServer(collection(db, `users/${companyId}/contacts`));
  return snapshot.docs.map(toContact);
}

// The same list, live: `onChange(contacts)` runs on every change until the
// returned unsubscribe is called. A listener that errors is finished (the SDK
// drops it), so `onError` is terminal and the caller decides whether to open a
// fresh one.
export function watch(companyId, onChange, onError) {
  return onSnapshot(
    collection(db, `users/${companyId}/contacts`),
    (snapshot) => onChange(snapshot.docs.map(toContact)),
    onError,
  );
}

// All writers of an address read the same ownership document, including when
// it does not exist yet. The rules require it and the contact to change together.
async function addressReference(companyId, emailNormalized) {
  const bytes = new TextEncoder().encode(emailNormalized);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  const key = Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0')).join('');
  return doc(db, `users/${companyId}/contact_addresses/${key}`);
}

const UNCERTAIN_WRITE_CODES = new Set([
  'unavailable', 'deadline-exceeded', 'internal', 'unknown', 'cancelled', 'aborted',
]);

async function runContactTransaction(contactRef, isCurrent, committedResult, operation) {
  try {
    try {
      return await runTransaction(db, operation);
    } catch (error) {
      if (error.code !== 'permission-denied') throw error;
      // Rules may see the new address owner before the SDK sees the conflict.
      // One fresh transaction distinguishes that race from a real denial.
      return await runTransaction(db, operation);
    }
  } catch (error) {
    if (error.code !== 'permission-denied' && !UNCERTAIN_WRITE_CODES.has(error.code)) {
      throw error;
    }
    let latest;
    try {
      latest = await getDocFromServer(contactRef);
    } catch (readError) {
      // Keep the original permission refusal. For a transport failure, neither
      // an absent local cache entry nor a failed read establishes the outcome.
      if (error.code === 'permission-denied') throw error;
    }
    const current = latest?.exists() ? latest.data() : undefined;
    if (error.code === 'permission-denied') {
      if (latest && !isCurrent(current)) throw new Error(CONTACT_CHANGED_MESSAGE);
      throw error;
    }
    if (current) {
      const result = committedResult(current);
      if (result) return result;
    }
    const uncertain = new Error(
      'Unable to verify whether the contact was saved. Check and retry this attempt before making another change.',
      { cause: error },
    );
    uncertain.code = 'contact-save-unverified';
    throw uncertain;
  }
}

function newContactDocument(contactId, displayName, email, emailNormalized, actorUid) {
  return {
    contact_id: contactId,
    display_name: displayName,
    email,
    email_normalized: emailNormalized,
    email_revision: 0,
    status: 'active',
    suppression_reason: null,
    suppressed_at: null,
    reactivated_at: null,
    provider_suppression_cleanup: null,
    deleted_at: null,
    created_at: serverTimestamp(),
    created_by: actorUid,
    updated_at: serverTimestamp(),
    updated_by: actorUid,
  };
}

// The dialog owns contactId for the whole attempt, including uncertain replies.
// Retrying can therefore recognise a committed creation without minting a second
// identity. A different holder is reported before either document is written.
export async function create(companyId, actorUid, displayName, email, contactId) {
  const cleanedName = cleanDisplayName(displayName);
  const { email: cleanEmail, email_normalized: emailNormalized } = canonicalizeEmail(email);
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(contactId)) {
    throw new Error('A valid contact ID is required.');
  }
  const contact = newContactDocument(contactId, cleanedName, cleanEmail, emailNormalized, actorUid);
  const contactRef = doc(db, `users/${companyId}/contacts/${contactId}`);
  const addressRef = await addressReference(companyId, emailNormalized);
  const committedResult = (current) => (
    current.contact_id === contactId && current.created_by === actorUid
      ? { status: 'created', contact: { ...current, id: contactId } } : null
  );

  return runContactTransaction(contactRef, (current) => !current, committedResult, async (tx) => {
    const snapshot = await tx.get(contactRef);
    if (snapshot.exists()) {
      const result = committedResult(snapshot.data());
      if (!result) throw new Error(CONTACT_CHANGED_MESSAGE);
      return result;
    }
    const ownership = await tx.get(addressRef);
    if (ownership.exists()) {
      const ownerId = ownership.data().contact_id;
      const owner = await tx.get(doc(db, `users/${companyId}/contacts/${ownerId}`));
      if (!owner.exists() || owner.data().email_normalized !== emailNormalized) {
        throw new Error('The contact address ownership record is inconsistent. Contact support.');
      }
      return {
        status: owner.data().status === 'active' ? 'exists' : 'reactivation_required',
        contact: { ...owner.data(), id: ownerId },
      };
    }
    tx.set(contactRef, contact);
    tx.set(addressRef, { contact_id: contactId });
    return { status: 'created', contact: { ...contact, id: contactId } };
  });
}

// Edit the display name in place. Deleted contacts are not editable.
export async function updateDisplayName(companyId, actorUid, contactId, displayName) {
  const cleanedName = cleanDisplayName(displayName);
  const contactRef = doc(db, `users/${companyId}/contacts/${contactId}`);

  await runTransaction(db, async (tx) => {
    const snapshot = await tx.get(contactRef);
    if (!snapshot.exists()) {
      throw new Error('This contact no longer exists.');
    }
    if (snapshot.data().status === 'deleted') {
      throw new Error('Deleted contacts cannot be edited.');
    }
    tx.update(contactRef, {
      display_name: cleanedName,
      updated_at: serverTimestamp(),
      updated_by: actorUid,
    });
  });

  return { contactId, display_name: cleanedName };
}

// Correct a contact's email address in place. Identity is the UUID, so this
// updates the same document's address fields and nothing else: Email Outputs
// keep pointing at this contact and resolve the corrected address on their next
// run. Historical Emails, attempts, and deliveries keep the original address.
export async function updateEmail(companyId, actorUid, contactId, correctedEmail, expectedEmailRevision) {
  const { email: cleanEmail, email_normalized: emailNormalized } = canonicalizeEmail(correctedEmail);
  if (
    !Number.isSafeInteger(expectedEmailRevision)
    || expectedEmailRevision < 0
    || expectedEmailRevision >= Number.MAX_SAFE_INTEGER
  ) {
    throw new Error(CONTACT_CHANGED_MESSAGE);
  }
  const editRevision = expectedEmailRevision + 1;
  const contactRef = doc(db, `users/${companyId}/contacts/${contactId}`);
  const newAddressRef = await addressReference(companyId, emailNormalized);
  const result = { contactId, email: cleanEmail, email_normalized: emailNormalized };
  const committedResult = (current) => (
    current.email_revision === editRevision && current.email === cleanEmail
      && current.email_normalized === emailNormalized && current.updated_by === actorUid
      ? result : null
  );
  const isCurrent = (current) => current?.email_revision === expectedEmailRevision;

  return runContactTransaction(contactRef, isCurrent, committedResult, async (tx) => {
    const snapshot = await tx.get(contactRef);
    if (!snapshot.exists()) {
      throw new Error('This contact no longer exists.');
    }
    const current = snapshot.data();
    if (committedResult(current)) return result;
    if (current.status === 'deleted') {
      throw new Error('Deleted contacts cannot be edited.');
    }
    if (!isCurrent(current)) {
      throw new Error(CONTACT_CHANGED_MESSAGE);
    }

    const oldAddressRef = await addressReference(companyId, current.email_normalized);
    const oldOwnership = await tx.get(oldAddressRef);
    if (!oldOwnership.exists() || oldOwnership.data().contact_id !== contactId) {
      throw new Error('The contact address ownership record is inconsistent. Contact support.');
    }
    if (current.email === cleanEmail) return result;
    if (oldAddressRef.path !== newAddressRef.path) {
      const newOwnership = await tx.get(newAddressRef);
      if (newOwnership.exists()) {
        throw new Error('Another contact already uses this email address.');
      }
      tx.set(newAddressRef, { contact_id: contactId });
      tx.delete(oldAddressRef);
    }
    tx.update(contactRef, {
      email: cleanEmail,
      email_normalized: emailNormalized,
      email_revision: editRevision,
      updated_at: serverTimestamp(),
      updated_by: actorUid,
    });
    return result;
  });
}

// Soft-delete a contact: lifecycle fields only. Functions and Triggers are never
// read or written — an Email Output still configured with this contact fails at
// execution with a bounded configuration error until the contact is restored or
// replaced in that Function.
export async function deleteContact(companyId, actorUid, contactId) {
  const contactRef = doc(db, `users/${companyId}/contacts/${contactId}`);

  await runTransaction(db, async (tx) => {
    const snapshot = await tx.get(contactRef);
    if (!snapshot.exists()) {
      throw new Error('This contact no longer exists.');
    }
    if (snapshot.data().status === 'deleted') return;
    tx.update(contactRef, {
      status: 'deleted',
      deleted_at: serverTimestamp(),
      updated_at: serverTimestamp(),
      updated_by: actorUid,
    });
  });

  return { contactId, status: 'deleted' };
}

// Reactivate a suppressed/deleted contact. Explicit and confirmed only, with
// actor/timestamp audit. Reactivation is refused while the provider suppression
// cleanup is still pending or has failed: until Mailgun has actually dropped the
// suppression, an active contact could not be sent to anyway. It NEVER enables
// Trigger Email and never touches Functions or Triggers.
export async function reactivate(companyId, actorUid, contactId, options = {}) {
  if (options.confirm !== true) {
    throw new Error('Reactivating a contact requires explicit confirmation.');
  }
  const contactRef = doc(db, `users/${companyId}/contacts/${contactId}`);

  await runTransaction(db, async (tx) => {
    const snapshot = await tx.get(contactRef);
    if (!snapshot.exists()) {
      throw new Error('This contact no longer exists.');
    }
    if (isSuppressionCleanupIncomplete(snapshot.data())) {
      throw new Error(CLEANUP_INCOMPLETE_MESSAGE);
    }
    tx.update(contactRef, {
      status: 'active',
      suppression_reason: null,
      suppressed_at: null,
      deleted_at: null,
      reactivated_at: serverTimestamp(),
      updated_at: serverTimestamp(),
      updated_by: actorUid,
    });
  });

  return { contactId, status: 'active' };
}

// --- suppression cleanup state ----------------------------------------------

// The provider suppression cleanup marker is written and advanced by the
// backend; the browser only reads it. `pending` and `failed` both mean the
// address is still suppressed at Mailgun, which blocks reactivation.
export const CLEANUP_INCOMPLETE_MESSAGE =
  'Decode is still removing the provider suppression for this address. Reactivation is available once that completes.';

export function isSuppressionCleanupIncomplete(contact) {
  const status = contact?.provider_suppression_cleanup?.status;
  return status === 'pending' || status === 'failed';
}
